import React, { useMemo, useEffect, useState, useRef } from 'react';
import ReactHtmlParser from "react-html-parser";

const ComparisonExtraCharges = ({ rfqData, sortedSuppliers, withGst, onTotalLowestPriceSumChange, closeRfqForm, isAdvancedTransport, selectedMaterialsMap, chargeInputs, setChargeInputs, selectedRatesTab, isEditRatesLocked, hiddenLineItemKeys = new Set(), getLineItemKey, renderLineItemHideButton }) => {
    // Use useMemo to calculate the total lowest price sum, only re-calculating when necessary
    const vendorCellRefs = useRef({});
    const [openRevisionTrail, setOpenRevisionTrail] = useState(() => new Set());

    const formatRevisionAmount = (value) =>
        new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value) || 0);

    const formatRevisionDate = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
    };

    const formatRevisionDifference = (difference, differencePercent) => {
        if (difference === null || difference === undefined) return "-";

        const numericDifference = Number(difference);
        if (!Number.isFinite(numericDifference)) return "-";

        return (
            <>
                {formatRevisionAmount(Math.abs(numericDifference))}{" "}
                {numericDifference < 0 && "\u25BC"}
                {numericDifference > 0 && "\u25B2"}
                {differencePercent !== null && differencePercent !== undefined && (
                    <span className="ml-1 text-[10px]">({Number(differencePercent).toFixed(2)}%)</span>
                )}
            </>
        );
    };

    const getRevisionChargeAmount = (quotationSet, chargeType) => {
        const charge = quotationSet?.otherCharges?.[chargeType] || {};
        const value = selectedRatesTab ? charge.selectedMrp ?? charge.mrp : charge.mrp;
        const amount = Number(value);
        return Number.isFinite(amount) ? amount : 0;
    };

    const getQuotationRemark = (quotationSet) => {
        const quotationUpdateReason = quotationSet?.quotationUpdateReason;
        if (typeof quotationUpdateReason === "string") return quotationUpdateReason;
        return quotationUpdateReason?.remark || quotationUpdateReason?.reason || "";
    };

    const getChargeRemark = (charge) => {
        const remark = charge?.remark ?? charge?.remarks ?? charge?.comments;
        if (typeof remark === "string") return remark.trim();
        return remark?.text || remark?.remark || "";
    };

    const formatChargeTypeLabel = (chargeType) =>
        chargeType
            .replace(/([A-Z])/g, " $1")
            .trim()
            .replace(/\b\w/g, character => character.toUpperCase());

    const getExtraChargeRevisionTrail = (supplier, chargeType) => {
        let previousSignature = null;

        return (supplier?.vendorQuotations?.quotationSets || [])
            .reduce((trail, quotationSet) => {
                const charge = quotationSet?.otherCharges?.[chargeType];
                if (!charge) return trail;

                const amount = getRevisionChargeAmount(quotationSet, chargeType);
                const signature = JSON.stringify({
                    amount,
                    gst: Number(charge?.gst || 0) || 0,
                });
                if (trail.length > 0 && signature === previousSignature) return trail;

                trail.push({
                    label: `R${trail.length}`,
                    date: formatRevisionDate(
                        quotationSet?.submittedAt || quotationSet?.updatedAt || quotationSet?.createdAt,
                    ),
                    submittedBy: quotationSet?.submittedBy || quotationSet?.updatedBy || "-",
                    amount,
                    gst: Number(charge?.gst || 0) || 0,
                    remark: getQuotationRemark(quotationSet),
                });
                previousSignature = signature;
                return trail;
            }, [])
            .map((revision, revisionIndex, trail) => {
                const previousRevision = trail[revisionIndex - 1];
                const difference = previousRevision
                    ? revision.amount - previousRevision.amount
                    : null;
                const differencePercent = previousRevision
                    ? previousRevision.amount !== 0
                        ? (Math.abs(difference) / Math.abs(previousRevision.amount)) * 100
                        : difference === 0
                            ? 0
                            : 100
                    : null;

                return {
                    ...revision,
                    label: `${revision.label}${revisionIndex === trail.length - 1 ? " (current)" : ""}`,
                    difference,
                    differencePercent,
                };
            });
    };

    const toggleRevisionTrail = (event, chargeType, vendorIndex, trailLength) => {
        event.stopPropagation();
        if (trailLength <= 1) return;

        const key = `${chargeType}-${vendorIndex}`;
        setOpenRevisionTrail((previous) => {
            const next = new Set(previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const totalLowestPriceSum = useMemo(() => {
        let totalSum = 0;

        // Loop through each charge type and calculate the lowest rate
        Object.keys(rfqData.otherCharges || {}).filter((chargeType) => rfqData.otherCharges[chargeType]).forEach((chargeType) => {
            let minCharge = Infinity;

            // Iterate through all suppliers to find the minimum charge for each charge type
            sortedSuppliers?.forEach((supplier) => {
                const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
                const otherCharges = vendorQuotation?.otherCharges || {};
                const chargeDetails = otherCharges[chargeType] || {};

                // Only consider valid charges (greater than 0)
                if (chargeDetails.mrp < minCharge) {
                    minCharge = chargeDetails.mrp;
                }
            });

            // Add the lowest charge to the total sum
            if (minCharge > 0 && !(isAdvancedTransport && chargeType.toLowerCase().includes("transport"))) {
                totalSum += minCharge;
            }
        });

        return totalSum;
    }, [rfqData, sortedSuppliers]); // Dependencies that trigger re-calculation

    useEffect(() => {
        // Call the parent callback to pass the updated total sum
        onTotalLowestPriceSumChange(totalLowestPriceSum);
    }, [totalLowestPriceSum, onTotalLowestPriceSumChange]);

    useEffect(() => {
        // Initialize chargeInputs with existing values
        const initialCharges = {};

        // Iterate through each supplier
        sortedSuppliers?.forEach((supplier) => {
            // Only proceed if the supplier is in the selectedMaterialsMap
            if (!selectedMaterialsMap?.[supplier.supplierId]) return;

            const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
            const otherCharges = vendorQuotation?.otherCharges || {};
            initialCharges[supplier.supplierId] = {};

            // Iterate over each charge type (like transport, loading, etc.)
            Object.keys(rfqData.otherCharges || {}).forEach((chargeType) => {
                let chargeAmount = selectedRatesTab ? otherCharges[chargeType]?.selectedMrp : otherCharges[chargeType]?.mrp || 0;

                // Handle special case for transport charge if isAdvancedTransport is true
                if ((closeRfqForm || selectedRatesTab) && isAdvancedTransport && chargeType.toLowerCase() === "transport") {
                    const selectedMaterialIds = selectedMaterialsMap?.[supplier.supplierId] || [];

                    // Calculate the sum of selectedTransportCost for the selected materials
                    chargeAmount = selectedMaterialIds
                        .map(material => {
                            // For each material, find its selectedTransportCost and add it to the sum
                            return material.selectedTransportCost || 0;
                        })
                        .reduce((sum, transportCost) => sum + transportCost, 0);
                }

                // Add the charge value to the initial charges for this supplier and charge type
                initialCharges[supplier.supplierId][chargeType] = chargeAmount;
            });
        });

        // Set the charge inputs state
        setChargeInputs(initialCharges);
    }, [sortedSuppliers, rfqData, closeRfqForm, isAdvancedTransport, selectedMaterialsMap, selectedRatesTab]);




    const handleInputChange = (supplierId, chargeType, value) => {
        setChargeInputs(prev => ({
            ...prev,
            [supplierId]: {
                ...prev[supplierId],
                [chargeType]: value
            }
        }));
    };

    return (
        <>
            {rfqData.otherCharges && (
                <tr>
                    <td
                        className="comparison-sticky-rfq-cell min-w-[480px] sticky left-0 z-40 bg-white border-r border-gray-300 px-6 py-2"
                    >
                        <div className="flex min-h-[28px] w-full items-start justify-between">
                            <p className="text-sm font-bold leading-5 text-[#2D2D11]">Extra Charges</p>
                            {(rfqData?.rfqType === "material" || rfqData?.rfqType === "labour") && <span className="text-xs pt-1 text-right">Lowest Rate</span>}
                        </div>
                    </td>
                    {sortedSuppliers?.map((supplier, index) => (
                        <td
                            key={index}
                            className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} px-5 border-b border-gray-300 ${index < sortedSuppliers?.length - 1 ? 'border' : ''}`}
                        >
                            {/* Placeholder for extra charges */}
                        </td>
                    ))}
                </tr>
            )}
            {Object.keys(rfqData.otherCharges || {}).filter(chargeType => rfqData.otherCharges[chargeType]).map(chargeType => {
                const lineItemKey = getLineItemKey?.("extra-charge", { id: chargeType }, chargeType) ?? `extra-charge:${chargeType}`;
                if (hiddenLineItemKeys.has(lineItemKey)) return null;
                const chargeTypeLabel = formatChargeTypeLabel(chargeType);
                let minCharge = Infinity;
                let minSupplierIndexes = [];
                sortedSuppliers?.forEach((supplier, vendorIndex) => {
                    const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
                    const otherCharges = vendorQuotation?.otherCharges || {};
                    let chargeAmount = otherCharges[chargeType]?.mrp || 0;

                    if ((closeRfqForm || selectedRatesTab) && isAdvancedTransport && chargeType.toLowerCase() === "transport") {
                        const selectedMaterialIds = selectedMaterialsMap?.[supplier.supplierId] || [];
                        const materialDetails = vendorQuotation?.materialDetails || [];
                        chargeAmount = materialDetails
                            .filter(material => selectedMaterialIds.includes(material.id))
                            .reduce((sum, material) => sum + (material.transportCost || 0), 0);
                    }

                    if (chargeAmount < minCharge) {
                        minCharge = chargeAmount;
                        minSupplierIndexes = [vendorIndex];
                    } else if (chargeAmount === minCharge) {
                        minSupplierIndexes.push(vendorIndex);
                    }
                });

                const chargeRemarks = sortedSuppliers?.map((supplier) => {
                    const latestQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
                    return getChargeRemark(latestQuotation?.otherCharges?.[chargeType]);
                }) || [];
                const hasChargeRemarks = chargeRemarks.some(Boolean);

                return (
                    <React.Fragment key={chargeType}>
                    <tr>
                        <td
                            className="comparison-sticky-rfq-cell sticky border-gray-300 border border-x border-l-0 left-0 z-40 align-top bg-white w-[300px]"
                        >
                            <div className="flex min-h-[28px] items-start justify-between px-6 py-2">
                                <div className="flex min-w-0 items-start gap-0.5">
                                    {renderLineItemHideButton?.(lineItemKey, chargeTypeLabel)}
                                    <p className="text-xs text-[#2D2D11] font-normal leading-5">
                                        {chargeTypeLabel}
                                        {isAdvancedTransport && chargeType === "transport" && (
                                            <span>: {rfqData?.transportOption === "perUnitAmount" ? "Per Unit" : "Lumpsum "}</span>
                                        )}
                                    </p>
                                </div>
                                <span
                                className="text-xs font-normal text-blue-700 cursor-pointer hover:underline"
                                onClick={(e) => {
                                    e.stopPropagation();

                                    if (!Number.isFinite(minCharge)) return;

                                    const lowestVendorIndex = minSupplierIndexes[0];
                                    if (lowestVendorIndex === undefined) return;

                                    const cell =
                                    vendorCellRefs.current[`${chargeType}-${lowestVendorIndex}`];
                                    if (!cell) return;

                                    const scrollContainer = cell.closest(".comparison-scrollbar");
                                    if (!scrollContainer) return;

                                    const stickyColumn = scrollContainer.querySelector(".fixed-column");
                                    const stickyWidth = stickyColumn?.offsetWidth || 420;

                                    const cellLeft =
                                    cell.getBoundingClientRect().left -
                                    scrollContainer.getBoundingClientRect().left;

                                    scrollContainer.scrollTo({
                                    left: scrollContainer.scrollLeft + cellLeft - stickyWidth,
                                    behavior: "smooth",
                                    });
                                }}
                                >
                                Rs. {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(minCharge)}
                                </span>
                            </div>
                        </td>
                        {sortedSuppliers?.map((supplier, index) => {
                            const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
                            const firstVendorQuotation = supplier.vendorQuotations?.quotationSets?.[0];
                            const revisionTrail = getExtraChargeRevisionTrail(supplier, chargeType);
                            const revisionCount = Math.max(revisionTrail.length - 1, 0);
                            const isRevisionTrailOpen = openRevisionTrail.has(`${chargeType}-${index}`);
                            const otherCharges = vendorQuotation?.otherCharges || {};
                            const firstOtherCharges = firstVendorQuotation?.otherCharges || {};
                            // Base charge from vendor
                            let chargeAmount = selectedRatesTab
                                ? (otherCharges[chargeType]?.selectedMrp ?? 0)
                                : (otherCharges[chargeType]?.mrp ?? 0) || 0;

                            // OVERRIDE if user edited this charge in input
                            const userEditedValue = chargeInputs?.[supplier.supplierId]?.[chargeType];
                            if (userEditedValue !== undefined && userEditedValue !== null && userEditedValue !== "") {
                                chargeAmount = Number(userEditedValue) || 0;
                            }
                            // First rate
                            const firstChargeAmount = firstOtherCharges[chargeType]?.mrp || 0;

                            // Calculate % diff
                            const difference = chargeAmount - firstChargeAmount;
                            // const percentageDifference = firstChargeAmount > 0
                            //     ? (difference / firstChargeAmount) * 100
                            //     : 0;

                            // Display arrow and cleaned % string
                            const showArrow =
                                difference < 0 ? '▼' :
                                    difference > 0 ? '▲' : '';

                            const percentColor =
                                difference < 0 ? 'text-green-600' :
                                    difference > 0 ? 'text-red-600' :
                                        'text-[#777777]';

                            const displayPercentage = `Rs. ${Math.abs(difference).toFixed(1)}`;

                            if ((closeRfqForm || selectedRatesTab) && isAdvancedTransport && chargeType.toLowerCase() === "transport") {
                                const selectedMaterialIds = selectedMaterialsMap?.[supplier.supplierId] || [];
                                const materialDetails = vendorQuotation?.materialDetails || [];
                                chargeAmount = selectedMaterialIds
                                    .map(item => item.materialId)
                                    .filter(materialId => materialDetails.some(material => material.id === materialId))
                                    .reduce((sum, materialId) => {
                                        const selectedMaterial = selectedMaterialIds.find(item => item.materialId === materialId);
                                        return sum + (selectedMaterial?.selectedTransportCost || 0);
                                    }, 0);
                            }

                            const isLowest = minSupplierIndexes.includes(index);
                            const showInputField = (closeRfqForm || selectedRatesTab) && (!isAdvancedTransport || chargeType.toLowerCase() !== "transport") && !isEditRatesLocked;


                            return (
                                <td
                                    ref={(el) => {
                                        if (el) {
                                        vendorCellRefs.current[`${chargeType}-${index}`] = el;
                                        }
                                    }}
                                   key={index} 
                                   className="w-[300px] px-4 py-2 align-top border-b border-r border-gray-300"
                                >
                                    <div className={`grid ${(closeRfqForm || selectedRatesTab) ? 'grid-cols-5' : 'grid-cols-4'} min-h-[28px] items-start gap-x-1 text-xs`}>
                                        {/* Charge amount */}
                                        {rfqData?.rfqType !== "contractor" && <p className="col-span-1 text-xs leading-5">Rs. {new Intl.NumberFormat("en-IN").format(chargeAmount)}</p>}

                                        {/* First quoted rate */}
                                        {rfqData?.rfqType !== "contractor" && <p className="col-span-1 text-center leading-5 text-[#555]">
                                            {firstChargeAmount > 0
                                                ? `Rs. ${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(firstChargeAmount)}`
                                                : '--'}
                                        </p>}

                                        {/* Percentage difference */}
                                        {rfqData?.rfqType !== "contractor" && <p className={`col-span-1 flex items-center justify-end gap-1 text-right leading-5 ${percentColor}`}>
                                            {displayPercentage}{showArrow}
                                        </p>}

                                        {/* Input field or placeholder */}
                                        {(selectedRatesTab || closeRfqForm) ? (
                                            <div className="col-span-1 flex items-center justify-end space-x-1">
                                                {(showInputField && rfqData?.rfqType !== "contractor") ? (
                                                    <input
                                                        type="text"
                                                        className="h-6 w-12 rounded border px-1 py-0 text-xs leading-5 focus:ring-0"
                                                        value={chargeInputs[supplier.supplierId]?.[chargeType] ?? chargeAmount}
                                                        onChange={(e) => handleInputChange(supplier.supplierId, chargeType, e.target.value)}
                                                    />
                                                ) : (
                                                    <div className="h-6 w-12" /> // Placeholder to preserve layout
                                                )}
                                            </div>
                                        ) : (
                                            (showInputField && rfqData?.rfqType !== "contractor") && (
                                                <input
                                                    type="text"
                                                    className="h-6 w-12 rounded border px-1 py-0 text-xs leading-5 focus:ring-0"
                                                    value={chargeInputs[supplier.supplierId]?.[chargeType] ?? chargeAmount}
                                                    onChange={(e) => handleInputChange(supplier.supplierId, chargeType, e.target.value)}
                                                />
                                            )
                                        )}


                                        {/* Final value */}
                                        <div className={`${rfqData?.rfqType !== "contractor" ? "col-span-1" : "col-span-4"} flex w-full items-start justify-end pr-2`}>
                                            <div className="flex flex-col items-end justify-center gap-1">
                                                <p className={`px-1 text-right text-xs ${isLowest ? "bg-lime-200 w-fit h-fit" : ""}`}>
                                                    Rs. {new Intl.NumberFormat("en-IN").format(chargeAmount)}
                                                </p>
                                                {revisionTrail.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2 py-[1px] text-[10px] font-semibold leading-4 text-blue-700 hover:border-blue-300 hover:bg-blue-100"
                                                        onClick={(event) => toggleRevisionTrail(event, chargeType, index, revisionTrail.length)}
                                                    >
                                                        {isRevisionTrailOpen
                                                            ? "Hide revisions"
                                                            : `${revisionCount} ${revisionCount === 1 ? "revision" : "revisions"}`}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {isRevisionTrailOpen && (
                                        <div
                                            className="mt-2 w-[calc(100%_+_24px)] min-w-[520px] rounded-xl border border-blue-100 bg-[#F8FAFC] px-3 py-3 text-left text-[11px] text-[#3F4B64] shadow-lg"
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            <div className="grid grid-cols-[38px_108px_minmax(75px,1fr)_minmax(90px,1fr)] gap-x-2 border-b border-gray-400 pb-2 text-[11px] font-semibold text-gray-500">
                                                <span>Round</span>
                                                <span className="text-center">Date</span>
                                                <span className="text-right">Charge</span>
                                                <span className="text-right">Difference</span>
                                            </div>
                                            {revisionTrail.map((revision, revisionIndex) => (
                                                <div
                                                    key={`${revision.label}-${revisionIndex}`}
                                                    className="border-b border-gray-300 py-2"
                                                >
                                                    <div className="grid grid-cols-[38px_108px_minmax(75px,1fr)_minmax(90px,1fr)] gap-x-2">
                                                        <span className={`font-semibold ${revisionIndex === revisionTrail.length - 1 ? "text-blue-700" : "text-gray-600"}`}>
                                                            {revision.label}
                                                        </span>
                                                        <span className="whitespace-normal break-words text-center leading-4">
                                                            <span className="block">{revision.submittedBy}</span>
                                                            <span className="block">{revision.date}</span>
                                                        </span>
                                                        <span className={`text-right font-semibold ${revisionIndex === revisionTrail.length - 1 ? "text-blue-700" : "text-[#2D2D11]"}`}>
                                                            Rs. {formatRevisionAmount(revision.amount)}
                                                        </span>
                                                        <span className={`text-right font-semibold ${revision.difference < 0 ? "text-emerald-600" : revision.difference > 0 ? "text-red-600" : "text-[#777777]"}`}>
                                                            {formatRevisionDifference(revision.difference, revision.differencePercent)}
                                                        </span>
                                                    </div>
                                                    {revision.remark && (
                                                        <p className="mt-1 rounded-md bg-white px-2 py-1 text-left text-[10px] leading-4 text-[#5F6F8A]">
                                                            <span className="font-semibold text-[#2D2D11]">Remarks:</span>{" "}
                                                            {revision.remark}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                </td>
                            );
                        })}
                    </tr>
                    {hasChargeRemarks && (
                        <tr key={`${chargeType}-remarks`} className="bg-white text-xs">
                            <td className="comparison-sticky-rfq-cell sticky left-0 z-40 border border-gray-300 bg-white px-[22px] py-2">
                                <p className="text-[11px] font-semibold italic text-[#6B7280]">Remarks</p>
                            </td>
                            {sortedSuppliers?.map((supplier, index) => {
                                const remark = chargeRemarks[index];
                                return (
                                    <td
                                        key={`${chargeType}-remark-${supplier.supplierId || index}`}
                                        className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} border border-gray-300 px-4 py-2`}
                                    >
                                        {remark ? (
                                            <p className="whitespace-normal break-words text-[11px] italic leading-4 text-[#555555]">
                                                {ReactHtmlParser(remark)}
                                            </p>
                                        ) : (
                                            <span className="text-[11px] text-gray-300">-</span>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    )}
                    </React.Fragment>
                );
            })}
        </>
    );
};

export default ComparisonExtraCharges;
